import { useCallback, useEffect, useRef, useState } from 'react'
import CoreChart, {
  CustomChartOptions,
} from '../../../VideoAnalysis/VideoAnalysisChart/components/CoreChart/CoreChart'
import styles from './ChartRangeView.module.less'
import { EChartsInstance } from 'echarts-for-react'
import cls from 'classnames'
import { useSize } from 'ahooks'

function toFixedNum(num: number, precision = 2): number {
  return parseFloat(num.toFixed(precision))
}

export interface ChartRangeViewProps {
  project: ProjectModels.ProjectDetail
  ranges: Array<{ start: number; end: number }>
  options?: CustomChartOptions
  className?: string
  chartClassName?: string
  onRender?: () => void
  style?: React.CSSProperties
  thumbnailMode?: 'default' | 'portrait' | 'portrait-ppt'
}

const PIC_MAX_HEIGHT = 36
const PIC_MARGIN_TOP = 8
const AXIS_TICK_GAP = 22

const sliceRanges = (rangePoints: Array<{ start: number; end: number }>, totalLength: number) => {
  const views = []
  let currentIndex = 1

  // 对 rangePoints 按 start 排序
  const sortedRanges = [...rangePoints].sort((a, b) => a.start - b.start)

  // 生成视图区间
  for (const range of sortedRanges) {
    // 添加 normal 区间
    if (currentIndex < range.start) {
      views.push({
        start: currentIndex,
        end: range.start - 1,
        type: 'normal',
      })
    }

    // 添加 target 区间
    views.push({
      start: range.start,
      end: range.end,
      type: 'target',
    })

    currentIndex = range.end + 1
  }

  // 添加最后的 normal 区间
  if (currentIndex <= totalLength) {
    views.push({
      start: currentIndex,
      end: totalLength,
      type: 'normal',
    })
  }

  return views
}

interface RangeView {
  x: number
  width: number
  height: number
  type: 'normal' | 'target'
  pics: Array<{
    x: number
    width: number
    src: string
  }>
}

function ChartRangeView(props: ChartRangeViewProps) {
  const [chartInstance, setChartInstance] = useState<EChartsInstance | null>(null)
  const [picRowHeight, setPicRowHeight] = useState(PIC_MAX_HEIGHT)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const rootSize = useSize(rootRef)
  const [points, setPoints] = useState<
    { x: number; y: number; frame: string; secondCount: number }[]
  >([])
  const ranges = props.ranges
  const length = props.project.frameInfos.length

  const [rangeViews, setRangeViews] = useState<RangeView[]>([])

  const rangeViewBoxHeight: number = rangeViews.length > 0
    ? rangeViews.find(r => r.type === 'target')!.height ||
    (points.length !== 0 &&
      points[0].y + AXIS_TICK_GAP + PIC_MARGIN_TOP + picRowHeight) as number
    /** 初始默认高度 */
    : 300

  const calculatePoints = useCallback(() => {
    if (!chartInstance) {
      return
    }
    setTimeout(() => {
      const heatMaps = props.project.detailMetrics.map(item => item.heatMap)
      const points = Array.from({ length: length }).map((_, index) => {
        const point = chartInstance.convertToPixel({ seriesIndex: 0 }, [index, 0])
        return {
          x: point[0],
          y: point[1],
          // frame: props.project.frameInfos[index].frame,
          frame: heatMaps[index],
          secondCount: Number(props.project.frameInfos[index].secondCount),
        }
      })
      setPoints(points)
    })
  }, [chartInstance])

  useEffect(() => {
    calculatePoints()
    chartInstance?.on('rendered', () => {
      calculatePoints()
    })
  }, [chartInstance])

  useEffect(() => {
    if (!points.length) {
      return
    }
    // console.log('ranges', ranges)
    const tmpRanges = ranges.map((range) => {
      return {
        ...range,
        // 这里和后端对了，比如后端给的 [2,5] 区间，实际上是 2 秒末到 5 秒末。
        start: range.start === 0 ? 0 : range.start + 1,
        end: range.end,
      }
    })
    const slicedRanges = sliceRanges(tmpRanges, points.length)
    // console.log('slicedRanges', slicedRanges)
    const tmp = slicedRanges.map((range) => {
      const frames = points.filter((p) => {
        return p.secondCount >= range.start && p.secondCount <= range.end
      })
      return {
        ...range,
        frames,
      }
    })
    // console.log('tmp', tmp)
    const xAxisOffset = points.length > 1 ? points[1].x - points[0].x : 0
    const axisLeft = toFixedNum(points[0].x - xAxisOffset * 0.5)
    const axisRight = toFixedNum(points[points.length - 1].x + xAxisOffset * 0.5)
    const slicedRangeViews: RangeView[] = tmp.map((range) => {
      const { frames } = range
      const width = frames.length * xAxisOffset
      return {
        // 按照设计稿，这里要往前挪一半 x 的刻度
        x: toFixedNum(frames[0].x) - xAxisOffset * 0.5,
        width,
        height: frames[0].y + AXIS_TICK_GAP + PIC_MARGIN_TOP + picRowHeight,
        type: range.type as 'target' | 'normal',
        pics: frames.map((f) => ({
          x: f.x,
          width: width / frames.length - 1,
          src: f.frame,
        })),
      }
    })
    /*
     * 这里虽然计算出了分片的 range view ，但是因为坐标的精度问题，导致分片不准确，所以需要手动处理一下
     * 各个区间的坐标，以保证是连续的。
     */
    const finalSlicedRangeViews = slicedRangeViews
      .map((view) => {
        return {
          ...view,
          width: view.type === 'target' ? Math.ceil(view.width) : view.width,
        }
      })
      .reduce((acc, curr) => {
        if (acc.length === 0) {
          return [{ ...curr, x: axisLeft }]
        } else {
          const last = acc[acc.length - 1]
          const x = last.x + last.width
          return [...acc, { ...curr, x }]
        }
      }, [] as RangeView[])
      .map((view, index, arr) => {
        if (index !== arr.length - 1) {
          return view
        }
        return {
          ...view,
          width: axisRight - view.x,
        }
      })

    setRangeViews(finalSlicedRangeViews)
  }, [points, ranges, picRowHeight])

  useEffect(() => {
    if (points.length > 0) {
      props.onRender?.()
    }
  }, [points])

  useEffect(() => {
    if (!rootRef.current) {
      return
    }
    const images = Array.from(rootRef.current.querySelectorAll('img'))
    if (images.length === 0) {
      return
    }
    const maxHeight = images.reduce((max, image) => {
      const height = image.getBoundingClientRect().height
      return height > max ? Math.ceil(height) : max
    }, PIC_MAX_HEIGHT)
    setPicRowHeight(maxHeight)
  }, [rangeViews, rootSize?.width])

  const xAxisY = points.length > 0 ? points[0].y : 0
  const picTop = xAxisY + AXIS_TICK_GAP + PIC_MARGIN_TOP

  return (
    <div
      ref={rootRef}
      className={cls(
        styles.chartRangeView,
        props.thumbnailMode === 'portrait' && styles.portraitThumbnails,
        props.thumbnailMode === 'portrait-ppt' && styles.portraitPptThumbnails,
        props.className
      )}
      style={{ height: rangeViewBoxHeight, ...props.style }}
    >
      {rangeViews.map((range, index) => {
        return (
          <div
            className={cls(styles.rangeView, styles[range.type])}
            key={index}
            style={{
              left: range.x,
              top: 0,
              width: range.width,
              height: range.height,
            }}
          >
            <div className={styles.rangePics} style={{ top: picTop }}>
              {range.pics.map((pic, index) => {
                return (
                  <div className={cls(styles.picBox, styles[range.type])} key={index}>
                    <img src={pic.src}
                      onLoad={() => {
                        if (!rootRef.current) {
                          return
                        }
                        const images = Array.from(rootRef.current.querySelectorAll('img'))
                        const maxHeight = images.reduce((max, image) => {
                          const height = image.getBoundingClientRect().height
                          return height > max ? Math.ceil(height) : max
                        }, PIC_MAX_HEIGHT)
                        setPicRowHeight(maxHeight)
                      }}
                      style={{ width: '100%', height: 'auto', display: 'block' }} alt='' />
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
      <div
        className={styles.chartCover}
        style={{
          top: picTop,
          height: `calc(100% - ${picTop}px)`,
        }}
      ></div>
      <CoreChart
        className={props.chartClassName}
        options={props.options}
        detailMetrics={props.project.detailMetrics}
        onChartReady={(chart) => {
          setChartInstance(chart as unknown as EChartsInstance)
        }}
      />
    </div>
  )
}

export default ChartRangeView
